import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { MotifFormField, MotifSelect, MotifOption } from "@ey-xd/motif-react";
import { routes } from "../../../routes/routes.constatns";
import { ArrowLeftIcon } from "../../../assets/Icons/ArrowLeft";
import { QualityFrameworkService } from "../../../api/services";
import { Tooltip } from "../../CommonUIComponents/Tooltip/Tooltip";
import {
  CELL_TRUNCATE_LENGTH,
  hasQualitySpocRole,
  truncateText,
} from "../qualityFramework.utils";
import { QualityFrameworkAccessDenied } from "../QualityFrameworkAccessDenied";
import "./AssignUsers.scss";

const renderTruncatedCell = (value) => {
  const text = String(value ?? "");

  if (text.length <= CELL_TRUNCATE_LENGTH) {
    return text;
  }

  return (
    <Tooltip
      trigger={<span className="au-truncated">{truncateText(text)}</span>}
      tooltipText={text}
      placement="top"
    />
  );
};

const buildRequestTitle = ({ clientName, country, analyzerType }) =>
  [clientName, country, analyzerType]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join("-");

export const AssignUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRoles = useSelector((state) => state.user?.userRoles);
  const rolesLoaded = useSelector((state) => state.user?.rolesLoaded);
  const hasAccess = hasQualitySpocRole(userRoles);
  const populationDetails = location.state?.populationDetails;
  const [assignTo, setAssignTo] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [orders, setOrders] = useState(populationDetails?.orders || []);
  const [removedIds, setRemovedIds] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    let isActive = true;

    if (!rolesLoaded || !hasAccess) {
      return undefined;
    }

    const loadEligibleReviewers = async () => {
      try {
        setIsLoadingReviewers(true);
        const response = await QualityFrameworkService.getEligibleReviewers();
        if (isActive) {
          setReviewers(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching eligible reviewers:", error);
        if (isActive) {
          setReviewers([]);
        }
      } finally {
        if (isActive) {
          setIsLoadingReviewers(false);
        }
      }
    };

    loadEligibleReviewers();

    return () => {
      isActive = false;
    };
  }, [rolesLoaded, hasAccess]);

  const visibleOrders = orders.filter(
    (order) => !removedIds.includes(order.orderId),
  );

  useEffect(() => {
    if (!selectedIds.length) {
      setAssignTo("");
    }
  }, [selectedIds]);
  const areAllVisibleOrdersAssigned =
    visibleOrders.length > 0 &&
    visibleOrders.every((order) => String(order.assignedTo || "").trim());

  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleAll = () =>
    setSelectedIds((prev) =>
      prev.length === visibleOrders.length
        ? []
        : visibleOrders.map((o) => o.orderId)
    );

  // Removed rows stay in state but are hidden and excluded from the assignment payload
  const handleDelete = (id) => {
    setRemovedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
  };

  const selectedCountries = new Set(
    visibleOrders
      .filter((order) => selectedIds.includes(order.orderId))
      .map((order) => order.country?.trim().toLowerCase())
      .filter(Boolean),
  );

  // A reviewer cannot review orders from a country they belong to
  const isReviewerEligible = (reviewer) => {
    if (!selectedCountries.size) return true;
    return !(reviewer.countryNames || "")
      .split(",")
      .map((country) => country.trim().toLowerCase())
      .filter(Boolean)
      .some((country) => selectedCountries.has(country));
  };

  const hasEligibleReviewer = reviewers.some(isReviewerEligible);

  const handleAssignToSelection = () => {
    if (!assignTo || !selectedIds.length) return;

    const selectedReviewer = reviewers.find(
      (reviewer) =>
        (reviewer.email ??
          reviewer.emailId ??
          reviewer.userPrincipalName ??
          reviewer.displayName) === assignTo,
    );

    if (selectedReviewer && !isReviewerEligible(selectedReviewer)) {
      setSubmitError(
        "The selected reviewer belongs to the same country as one of the selected orders.",
      );
      return;
    }

    setSubmitError("");
    setOrders((prev) =>
      prev.map((order) =>
        selectedIds.includes(order.orderId)
          ? { ...order, assignedTo: assignTo }
          : order,
      ),
    );
    setSelectedIds([]);
    setAssignTo("");
  };

  const handleReset = () => {
    setOrders((prev) =>
      prev.map((order) =>
        selectedIds.includes(order.orderId)
          ? { ...order, assignedTo: "" }
          : order,
      ),
    );
    setSubmitError("");
  };

  const handleSubmit = async () => {
    if (!areAllVisibleOrdersAssigned) {
      setSubmitError("Please assign a reviewer to all visible orders.");
      return;
    }

    // API expects one entry per reviewer with all the orders assigned to them
    const ordersByReviewer = new Map();
    visibleOrders.forEach((order) => {
      if (!order.assignedTo) return;
      if (!ordersByReviewer.has(order.assignedTo)) {
        ordersByReviewer.set(order.assignedTo, []);
      }
      ordersByReviewer.get(order.assignedTo).push(order.orderId);
    });

    const payload = Array.from(ordersByReviewer.entries()).map(
      ([qualityReviewerEmail, orderIds]) => ({ orderIds, qualityReviewerEmail }),
    );

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await QualityFrameworkService.assignUsers(payload);
      navigate(`/${routes.qualityFramework}`);
    } catch (error) {
      console.error("Error assigning users:", error);
      setSubmitError("Unable to assign users. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (rolesLoaded && !hasAccess) {
    return <QualityFrameworkAccessDenied onBack={() => navigate(-1)} />;
  }

  return (
    <div className="assign-users-page">
      <div className="assign-users-header">
        <div className="assign-users-header-left">
          <button
            type="button"
            className="assign-users-back-btn"
            onClick={() => navigate(`/${routes.qualityFrameworkCreate}`)}
          >
            <ArrowLeftIcon width={12} height={12} color="#A4A3B1" />
            Back
          </button>
          <h1 className="assign-users-title">Assign User</h1>
        </div>
        <div className="assign-users-header-right">
          <MotifFormField className="assign-users-select-field">
            <MotifSelect
              value={assignTo}
              onChange={(val) => setAssignTo(val)}
              placeholder={
                isLoadingReviewers ? "Loading..." : "Assign To"
              }
              disabled={isLoadingReviewers || !selectedIds.length}
              filter
            >
              {!isLoadingReviewers && !hasEligibleReviewer ? (
                <MotifOption value="__no_eligible_reviewer__" disabled>
                  No eligible reviewer is available
                </MotifOption>
              ) : (
                reviewers.map((reviewer) => {
                  const email =
                    reviewer.email ??
                    reviewer.emailId ??
                    reviewer.userPrincipalName ??
                    reviewer.displayName;
                  return (
                    <MotifOption
                      key={reviewer.userId ?? email}
                      value={email}
                      disabled={!isReviewerEligible(reviewer)}
                    >
                      {reviewer.displayName || email}
                    </MotifOption>
                  );
                })
              )}
            </MotifSelect>
          </MotifFormField>
          <button
            type="button"
            className="assign-users-assign-btn"
            onClick={handleAssignToSelection}
            disabled={!assignTo || !selectedIds.length}
          >
            Assign to Selection
          </button>
        </div>
      </div>

      <div className="assign-users-content">
        <h2 className="assign-users-section-title">Order Details</h2>
        {visibleOrders.length === 0 ? (
          <div className="assign-users-empty-state">No records found</div>
        ) : (
          <div className="assign-users-table-wrapper">
          <table className="assign-users-table">
            <thead>
              <tr>
                <th className="au-th au-col-check">
                  <input
                    type="checkbox"
                    className="au-checkbox"
                    checked={selectedIds.length === visibleOrders.length && visibleOrders.length > 0}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="au-th">Order ID</th>
                <th className="au-th">Request Title</th>
                <th className="au-th">Engagement Name</th>
                <th className="au-th">Area</th>
                <th className="au-th">Super Region</th>
                <th className="au-th">Country</th>
                <th className="au-th">Analyzer type</th>
                <th className="au-th">Preparer</th>
                <th className="au-th">Reviewer</th>
                <th className="au-th">Assigned to</th>
                <th className="au-th au-col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((row) => (
                <tr
                  key={row.orderId}
                  className={`au-tr${selectedIds.includes(row.orderId) ? " selected" : ""}`}
                >
                  <td className="au-td au-col-check">
                    <input
                      type="checkbox"
                      className="au-checkbox"
                      checked={selectedIds.includes(row.orderId)}
                      onChange={() => toggleSelect(row.orderId)}
                      aria-label={`Select order ${row.orderId}`}
                    />
                  </td>
                  <td className="au-td au-order-id">AX-{row.orderId}</td>
                  <td className="au-td">
                    {renderTruncatedCell(buildRequestTitle(row))}
                  </td>
                  <td className="au-td">
                    {renderTruncatedCell(row.engagementName)}
                  </td>
                  <td className="au-td">{row.area}</td>
                  <td className="au-td">{row.superRegion}</td>
                  <td className="au-td">{row.country}</td>
                  <td className="au-td">{row.analyzerType}</td>
                  <td className="au-td">{row.preparer}</td>
                  <td className="au-td">{row.reviewer}</td>
                  <td className="au-td">{row.assignedTo || "—"}</td>
                  <td className="au-td au-col-action">
                    <button
                      type="button"
                      className="au-delete-btn"
                      aria-label={`Remove order ${row.orderId}`}
                      onClick={() => handleDelete(row.orderId)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="assign-users-footer">
        {submitError && (
          <span className="assign-users-error">{submitError}</span>
        )}
        <button
          type="button"
          className="assign-users-cancel-btn"
          onClick={handleReset}
          disabled={!selectedIds.length}
        >
          Reset
        </button>
        <button
          type="button"
          className="assign-users-submit-btn"
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            !areAllVisibleOrdersAssigned ||
            visibleOrders.length === 0
          }
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  );
};

export default AssignUsers;
